import { useCallback, useState } from 'react';
import { InsightsDashboardReport } from 'src/components/enterprise/features/HomePage/BusinessAdminInsights/types/InsightsDashboard';
import { ReportFiltersPayload } from 'src/components/enterprise/features/HomePage/BusinessAdminInsights/types/reportFilters';
import { fetchSingleInsightsReport as fetchSingleInsightsReportApi } from 'src/components/enterprise/features/HomePage/BusinessAdminInsights/api/singleInsightsReport';
import { useRequestTracker } from 'src/components/enterprise/features/HomePage/BusinessAdminInsights/hooks/useRequestTracker';

export type UseFetchSingleInsightsReportReturnType = {
  isLoading: boolean;
  hasError: boolean;
  singleInsightsReport: InsightsDashboardReport | null;
  fetchSingleInsightsReport: (payload: ReportFiltersPayload) => Promise<void>;
};

export const useFetchSingleInsightsReport = (
  corpId: string,
  reportName: string
): UseFetchSingleInsightsReportReturnType => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const [singleInsightsReport, setSingleInsightsReport] =
    useState<InsightsDashboardReport | null>(null);

  const { startRequest } = useRequestTracker();

  const handleMissingParams = useCallback((): void => {
    setSingleInsightsReport(null);
    setHasError(true);
    setIsLoading(false);
  }, []);

  const fetchSingleInsightsReport = useCallback(
    async (payload: ReportFiltersPayload): Promise<void> => {
      const isStale = startRequest();

      if (!corpId || !reportName) {
        handleMissingParams();

        return;
      }

      try {
        setIsLoading(true);
        setHasError(false);

        const response = await fetchSingleInsightsReportApi(
          corpId,
          reportName,
          payload
        );

        if (!isStale()) {
          setSingleInsightsReport(response);
        }
      } catch (error) {
        if (!isStale()) {
          setHasError(true);
        }
      } finally {
        if (!isStale()) {
          setIsLoading(false);
        }
      }
    },
    [corpId, reportName, handleMissingParams, startRequest]
  );

  return {
    isLoading,
    hasError,
    singleInsightsReport,
    fetchSingleInsightsReport,
  };
};
